/**
 * Chunk sizing for the paged CSV exports (RSP-5650, revised by CUS-314).
 *
 * A Lambda synchronous invocation caps its response at 6 MB, so the reporting
 * exports page rather than asking for every row at once. The cap is a BYTE
 * limit, but the chunk size was a fixed 5,000 ROWS tuned against narrow
 * single-datasource reports. Wide reports blew straight through it: a 35-column
 * report with nine `json_agg` array columns failed at 5,000 rows for every user
 * who tried it, while a 16-column report over the same data succeeded seconds
 * later. The Lambda logged a clean success both times — the response simply
 * could not be delivered, and Hasura surfaced it as a bare `internal error`.
 *
 * 16 columns x 5,000 rows is known to fit and 35 x 5,000 is known not to, so the
 * boundary sits somewhere between 80,000 and 175,000 cells. {@link
 * TARGET_CELLS_PER_CHUNK} is deliberately set at HALF the known-good figure
 * rather than at it: the 16-column export may itself have been close to the
 * limit, and sizing to the last value that happened to work leaves no margin.
 * Sizing by column count makes a wide report ask for proportionally fewer rows.
 *
 * The cost of the margin is round trips, which are cheap and sequential; the cost
 * of getting it wrong is a failed export, which is what this fixes.
 *
 * This is a heuristic, not a byte count — cell width still varies with content —
 * so callers must halve and retry on failure via {@link nextExportChunkSize}
 * rather than treating the first size as correct.
 */
export const MAX_EXPORT_CHUNK_SIZE = 5000;
export const MIN_EXPORT_CHUNK_SIZE = 250;
export const TARGET_CELLS_PER_CHUNK = 40_000;

/**
 * Rows to request for the first chunk of a report with `columnCount` columns.
 * Clamped to [MIN, MAX] so a very wide report still makes progress and a narrow
 * one keeps the original 5,000-row behaviour.
 */
export const initialExportChunkSize = (columnCount: number): number => {
  // A non-positive count would divide to Infinity; treat it as a single column
  // so the result clamps to MAX rather than becoming NaN.
  const columns = Math.max(Math.floor(columnCount) || 1, 1);

  return Math.min(
    MAX_EXPORT_CHUNK_SIZE,
    Math.max(
      MIN_EXPORT_CHUNK_SIZE,
      Math.floor(TARGET_CELLS_PER_CHUNK / columns)
    )
  );
};

/**
 * Next size to try after a chunk of `limit` rows failed: halve, with MIN as the
 * floor. Callers stop retrying once the failing limit is already at the floor,
 * so this never returns a size that would loop forever.
 */
export const nextExportChunkSize = (limit: number): number =>
  Math.max(MIN_EXPORT_CHUNK_SIZE, Math.floor(limit / 2));

/**
 * What one chunk fetch produced.
 *  - `rows`: the chunk came back. Fewer rows than the requested limit means the
 *    end of the result set.
 *  - `failure`: the request failed, or returned neither data nor an error (which
 *    the lazy query hooks do for a throw they cannot classify, and which must
 *    NOT be read as an empty page — that yields a truncated export).
 *  - `abort`: the caller cannot build a request at all, e.g. the datasource is
 *    not loaded. Silent: no error to report.
 */
export type ExportChunkOutcome<TRow> =
  | { rows: TRow[] }
  | { failure: unknown }
  | { abort: true };

/**
 * Run one chunk fetch and classify the result.
 *
 * Kept here rather than inline at each call site so both export paths agree on
 * what counts as a failure. Two rules that are easy to get wrong independently:
 *
 *  - "neither data nor error" is a FAILURE, not an empty page. The lazy query
 *    hooks return that shape for a throw they cannot classify, and reading it as
 *    end-of-results produces a silently truncated export.
 *  - the GraphQL branch of the lazy hooks returns Apollo's result without
 *    catching, so the underlying call can reject. Uncaught, that escapes the
 *    export loop entirely — no error handler, no notification, no file.
 *
 * Generic over the query result so this file stays free of app data types.
 */
export const fetchChunkOutcome = async <TData, TRow>(
  run: () => Promise<{ data?: TData | null; error?: unknown }>,
  selectRows: (data: TData) => TRow[]
): Promise<ExportChunkOutcome<TRow>> => {
  try {
    const result = await run();
    if (result.error || !result.data) {
      return {
        failure:
          result.error ??
          new Error('Reporting request returned neither data nor an error'),
      };
    }

    return { rows: selectRows(result.data) };
  } catch (e) {
    return { failure: e };
  }
};

export type CollectExportRowsResult<TRow> =
  /**
   * `truncated` means paging stopped because it hit `rowCap`, not because the
   * result set ended — so rows exist that are NOT in `rows`. Callers must tell
   * the user; a capped export that presents as successful is the silent-data-loss
   * failure this module exists to prevent.
   */
  | { status: 'complete'; rows: TRow[]; truncated: boolean }
  | { status: 'aborted' }
  | {
      status: 'failed';
      failure: unknown;
      offset: number;
      limit: number;
      rowsCollected: number;
    };

/**
 * Page a reporting result set into one row array (CUS-314).
 *
 * Shared by the CDS detail export and the dashboard CDS table widget export,
 * which previously duplicated this loop and so duplicated its bugs.
 *
 * Two behaviours worth stating explicitly, because the previous loop got both
 * wrong:
 *
 *  - **A failed chunk shrinks and retries the same offset** rather than
 *    abandoning the export. Chunk sizing from column count is a heuristic (see
 *    {@link initialExportChunkSize}), so an over-estimate must cost a retry, not
 *    the whole download. Retries are bounded: the size halves each time and the
 *    loop gives up once a failure happens at {@link MIN_EXPORT_CHUNK_SIZE}.
 *  - **A failure is never treated as end-of-results.** Returning partial rows
 *    would produce a CSV that looks complete but silently isn't, which is worse
 *    than an error message.
 */
export const collectExportRows = async <TRow>({
  columnCount,
  rowCap,
  fetchChunk,
}: {
  columnCount: number;
  rowCap: number;
  fetchChunk: (paging: {
    offset: number;
    limit: number;
  }) => Promise<ExportChunkOutcome<TRow>>;
}): Promise<CollectExportRowsResult<TRow>> => {
  const rows: TRow[] = [];
  let chunkSize = initialExportChunkSize(columnCount);
  let offset = 0;

  while (offset < rowCap) {
    const limit = Math.min(chunkSize, rowCap - offset);
    const outcome = await fetchChunk({ offset, limit });

    if ('abort' in outcome) {
      return { status: 'aborted' };
    }

    if ('failure' in outcome) {
      if (limit > MIN_EXPORT_CHUNK_SIZE) {
        chunkSize = nextExportChunkSize(limit);
        continue;
      }

      return {
        status: 'failed',
        failure: outcome.failure,
        offset,
        limit,
        rowsCollected: rows.length,
      };
    }

    rows.push(...outcome.rows);
    if (outcome.rows.length < limit) {
      // A short chunk is the end of the result set, so nothing was left behind.
      return { status: 'complete', rows, truncated: false };
    }
    offset += limit;
  }

  // Falling out of the loop means every chunk came back full right up to the cap,
  // which is ambiguous: the result set might be exactly `rowCap` rows, in which
  // case nothing was omitted and warning the user would be wrong. Probe a single
  // row past the cap to tell the two apart. That costs one extra request, and
  // only in the rare case the cap is actually reached.
  if (rowCap <= 0) {
    return { status: 'complete', rows, truncated: false };
  }

  const probe = await fetchChunk({ offset: rowCap, limit: 1 });

  // `abort` means the caller can no longer build a request — the datasource has
  // gone, or the surface it belonged to has. Honour it here exactly as the data
  // path does, rather than quietly downgrading it to a complete export. That
  // does discard a full `rowCap` rows, which is the point: an abort says the
  // caller no longer wants a file, and producing one anyway would be worse than
  // producing nothing.
  if ('abort' in probe) {
    return { status: 'aborted' };
  }

  // A probe that FAILED is different from one that aborted: the export is still
  // wanted, we just cannot tell whether rows were left behind. Err towards
  // "truncated", because the two mistakes are not symmetric — a spurious warning
  // is an annoyance, whereas presenting an incomplete export as complete is the
  // silent data loss this guards against.
  const truncated = 'rows' in probe ? probe.rows.length > 0 : true;

  return { status: 'complete', rows, truncated };
};
